import { useDispatch, useSelector } from 'react-redux';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { BackTop } from 'antd';
import { Common } from '@stockbit/ui';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import VisibilitySensor from 'react-visibility-sensor';
import {
  actions as wsActions,
  streamWsSelector,
  wsKeySelector,
} from 'global/WebSocket/slice';
import moment from 'moment';
import { selectors as userSelectors } from 'features/user/slice';
import QueryProvider from 'global/QueryProvider';
import NotesCompose from 'global/NotesCompose';
import auth from 'utils/auth';
import streamWidget, { effects, selectors } from './slice';

import Skeleton from './Skeleton';
import SkeletonNews from './Skeleton/News';
import SkeletonResearch from './Skeleton/Research';
import Post from './Post';
import PostNews from './NewsPost';
import Navigation from './Navigation';
import {
  StreamNewPostBox,
  StreamNewPostText,
  StreamWidgetBox,
  BackTopWrapper,
  LineLoader,
} from './styled';
import {
  reportCategories,
  streamTypes,
  STREAM_TABS,
  DEFAULT_TYPE,
  STREAM_TYPE,
} from './constants';
import {
  getStreamCategories,
  getFilterParamFromLocalStorage,
  handleAnalyticsTabView,
} from './utils';
import StreamConversation from '../StreamConversation';
import EmptyState from './EmptyState';
import SuggestedPeopleWidget from '../SuggestedPeopleWidget';
import ResearchPost from './ResearchPost';
import EmptyResearch from './EmptyState/EmptyResearch';
import EmptySearch from './EmptyState/EmptySearch';
import { StreamEntryPoint } from './types';
import EmptyCompanyPost from './EmptyState/EmptyCompanyPost';
import BlockedState from './BlockedState';
import NotesPost from './NotesPost';
import Image from 'global/Image';

const UnboxingStockWidget = dynamic(
  () => import('global/UnboxingStockWidget'),
  { ssr: false },
);

const { Iconmoon } = Common;
interface StreamWidgetProps {
  type?: 'generic' | 'user' | 'symbol';
  username?: string;
  symbol?: string;
  hideNavigation?: boolean;
  noBorder?: boolean;
  hasDefaultTab?: boolean;
  sentiment?: ReactNode;
  entryPoint: StreamEntryPoint;
  isUserBlocked?: boolean;
  companyName?: string;
}

/**
 * Stream Widget component
 * @param {Object} props
 * @param {'generic' | 'user' | 'symbol'} props.type
 * @param {string} props.username
 * @param {string} props.symbol
 * @param {boolean} props.hideNavigation
 * @param {boolean} props.noBorder
 * @param {boolean} props.hasDefaultTab - is current navigation tab persisted in browser
 * @param {boolean} props.isUserBlocked
 * @param {string} props.companyName
 */
function StreamWidget({
  type = 'generic',
  username = '',
  symbol = '',
  hideNavigation = false,
  noBorder = false,
  hasDefaultTab = false,
  sentiment = null,
  entryPoint,
  isUserBlocked,
  companyName,
}: StreamWidgetProps) {
  const [STREAM, NEWS, RESEARCH] = STREAM_TABS;
  const router = useRouter();

  const dispatch = useDispatch();
  const {
    list: listStream,
    listResearch,
    error,
    isLoading,
    isLoadingMore,
    filterParams,
    togglePinPost,
    newPostCount,
    tabHeader,
    companyPinnedPost,
  } = useSelector(selectors);

  const {
    pinnedPost: { data: pinnedPost },
  } = useSelector(userSelectors);

  const isResearchStream =
    tabHeader === RESEARCH.value || filterParams.category === RESEARCH.value;
  const isNotesTab = filterParams.category === 'notes';

  const list = useMemo(() => {
    if (isResearchStream) return listResearch;
    if (isNotesTab) return [];
    return listStream;
  }, [isResearchStream, listResearch, isNotesTab, listStream]);

  // websocket selectors
  const wskey = useSelector(wsKeySelector);
  const streamSocket = useSelector(streamWsSelector);

  const streamCategories = getStreamCategories(type);
  const localFilter = hasDefaultTab ? getFilterParamFromLocalStorage() : null;

  // Use post's template and its skeleton for common post, news, or research
  const isGenericNews =
    (type === 'generic' && tabHeader === NEWS.value) || false;
  const PostItem = isGenericNews ? PostNews : Post;
  let SkeletonItem = Skeleton;
  if (isResearchStream) SkeletonItem = SkeletonResearch;
  if (isGenericNews) SkeletonItem = SkeletonNews;

  const getFilterParamsData = () => {
    const {
      reporttype,
      category,
      type: filterType,
      date,
      ...rest
    } = filterParams;

    let data: any = {
      ...rest,
      category,
      streamType: type,
      username,
      symbol,
      type: filterType,
      tabHeader,
    };

    if (hasDefaultTab) {
      data = {
        ...data,
        ...localFilter,
      };
    }

    if (tabHeader === NEWS.value) {
      data.category = NEWS.value;
    }

    if (category === 'reports') {
      data.reporttype = reporttype || reportCategories[0].key;
    }

    if (filterType === 'trending') {
      data.date = data.date || moment(Date.now()).format('YYYY-MM-DD');
    }

    if (data.category !== DEFAULT_TYPE) {
      data.type = DEFAULT_TYPE;
    }

    if (isResearchStream) {
      return {
        keyword: data.keyword,
        symbol: symbol || undefined,
      };
    }

    return data;
  };

  const [page, setPage] = useState<number>(1);
  const [openConversation, setOpenConversation] = useState<boolean>(false);
  const [selectedPostId, setSelectedPostId] = useState<number>(null);
  const [selectedParentId, setSelectedParentId] = useState<number>(null);
  const [isLastPage, setIsLastPage] = useState<boolean>(false);
  const [lastReplyId, setLastReplyId] = useState<number | null>(null);

  const handleGetStream = () => {
    // category and type changed, get initial data
    const params = getFilterParamsData();

    if (isResearchStream) {
      dispatch(
        effects.getResearchStream({
          ...params,
        }),
      );
    } else if (!isNotesTab) {
      dispatch(
        effects.getStream({
          ...params,
        }),
      );
    }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  // Clear all filter before open page
  // Clear all filter when username changed
  useEffect(
    () => () => {
      dispatch(streamWidget.actions.resetFilterParam());
      // @ts-ignore
      dispatch(streamWidget.actions.setTabHeader(STREAM.value));
    },
    [username],
  );

  // used to set default filter params
  useEffect(() => {
    if (filterParams.category === '') {
      const defaultFilter = {
        category: streamCategories[0].key,
        type: streamTypes[1].key, // filter 'Followed' as default
      };
      let filter = getFilterParamsData();
      if (hasDefaultTab) {
        filter = {
          ...defaultFilter,
          ...localFilter,
        };
        if (localFilter.tabHeader) {
          dispatch(streamWidget.actions.setTabHeader(localFilter.tabHeader));
        }
      } else {
        filter = defaultFilter;
      }
      dispatch(streamWidget.actions.setFilterParam(filter));
    } else {
      // category and type changed, get initial data
      handleGetStream();

      const filterType =
        filterParams.category === 'reports'
          ? filterParams.reporttype
          : filterParams.type;
      if (filterType !== null) {
        handleAnalyticsTabView(filterParams.category, filterType);
      }

      // reset for loadmore action
      setIsLastPage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filterParams, tabHeader, symbol, isNotesTab]);

  useEffect(() => {
    const EXCLUDED_CATEGORIES = ['prediction', 'insider', ''];
    if (!EXCLUDED_CATEGORIES.includes(filterParams.category)) {
      const filter = filterParams.category.toUpperCase();
      let channel = '*';

      if (symbol) {
        channel = `${symbol}`;
      } else if (username) {
        channel = `P:${username}`;
      }

      // update config if websocket has been connected
      if (wskey) {
        dispatch(
          // @ts-ignore
          wsActions.UPDATE_CHANNELS_CONFIG({
            stream: [`${channel}.${filter}`],
          }),
        );
      }
    }

    return () => {
      // reset ws config on unmount
      dispatch(
        // @ts-ignore
        wsActions.UPDATE_CHANNELS_CONFIG({
          stream: [],
        }),
      );
    };
  }, [dispatch, filterParams, symbol, username, wskey]);

  useEffect(() => {
    if (streamSocket) {
      dispatch(streamWidget.actions.addNewPostCount());
    }
  }, [dispatch, streamSocket]);

  const handleLoadMoreStream = async () => {
    const lastPost = list[list.length - 1];
    const { postid, last_reply_date: lastReplyDate } = lastPost;
    const params = getFilterParamsData();

    if (filterParams.type === 'trending') {
      params.page = page + 1;
      setPage(page + 1);
    } else {
      params.beforetimestamp = lastReplyDate;
    }

    params.lastpost = postid;

    const loadMore = await dispatch(
      effects.loadMoreStream({
        ...params,
      }),
    );
    // @ts-ignore
    const { data, error: errorLoadMore } = loadMore.payload;

    if (!errorLoadMore && data.length === 0) {
      setIsLastPage(true);
    }
  };

  const handleLoadMoreResearch = async () => {
    const lastPost = list[list.length - 1];
    const { id } = lastPost;

    const params = getFilterParamsData();

    params.last_id = id;

    const loadMore = await dispatch(effects.getResearchStream(params));

    // @ts-ignore
    const { data: researchData } = loadMore.payload;

    if (researchData?.length === 0) {
      setIsLastPage(true);
    }
  };

  const handleLoadMore = async (isVisible: boolean) => {
    const loadingData = isLoading || isLoadingMore;

    if (isVisible && !loadingData && list.length > 0) {
      if (isResearchStream) {
        handleLoadMoreResearch();
        return;
      }
      if (isNotesTab) return;

      handleLoadMoreStream();
    }
  };

  // handle pin post
  useEffect(() => {
    if (togglePinPost.data && !togglePinPost.error) {
      // success pin/unpin stream
      handleGetStream();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, togglePinPost.data, togglePinPost.error]);

  // get company pinned post
  useEffect(() => {
    if (symbol && type === 'symbol') {
      dispatch(effects.getCompanyPinnedPost(symbol));
    }
  }, [symbol]);

  const handleOpenConversation = (post) => {
    setSelectedPostId(post?.postid);
    setSelectedParentId(post?.parent_postid);
    setLastReplyId(post?.last_reply?.postid);
  };

  const handleCloseConversation = () => {
    setOpenConversation(false);
    setSelectedPostId(null);
  };

  const handleDiscover = () => {
    router.push('/discover/people');
  };

  const hasCompanyPinnedPost = useMemo(
    () =>
      filterParams.category !== 'research' &&
      type === 'symbol' &&
      companyPinnedPost?.data?.postid &&
      !isNotesTab,
    [filterParams, type, companyPinnedPost, isNotesTab],
  );

  const isSelfStream = useMemo(() => {
    const { username: myUsername = '' } = auth.getUserAccess() as unknown as {
      username: string;
    };

    return username === myUsername;
  }, [username]);

  const renderContent = () => {
    if (isLoading) {
      return [...new Array(5)].map(() => <SkeletonItem />);
    }

    if (type === 'user' && entryPoint === 'user' && username && isUserBlocked) {
      return <BlockedState username={username} />;
    }

    // Render Notes
    if (isNotesTab && type === 'symbol') {
      return <NotesCompose defaultSymbol={symbol} companyName={companyName} />;
    }

    if (isNotesTab && type === 'user' && isSelfStream) {
      return <NotesPost />;
    }

    if ((error && list.length === 0) || list.length === 0) {
      // Research Empty
      if (isResearchStream && filterParams.keyword?.length === 0) {
        return <EmptyResearch />;
      }

      // Search Result Empty
      if (filterParams.keyword?.length > 0) {
        return <EmptySearch />;
      }

      if (type === STREAM_TYPE.SYMBOL) {
        return <EmptyCompanyPost />;
      }

      // Stream Empty
      return <EmptyState onDiscover={handleDiscover} />;
    }

    // Render stream research post
    if (isResearchStream) {
      return list.map((post) => <ResearchPost post={post} />);
    }

    // Render stream
    return list.map((post, i) => {
      // Hide pinned post in company/user stream list post
      const isPinnedPost =
        post.ispinned === 1 || post.postid === pinnedPost[username]?.postid;

      if (type !== 'generic' && isPinnedPost) {
        return null;
      }

      if (
        hasCompanyPinnedPost &&
        post.postid === companyPinnedPost?.data?.postid
      ) {
        return null;
      }

      return (
        <>
          {i === 10 && type === 'generic' && <UnboxingStockWidget />}
          <PostItem
            post={post}
            key={post.postid}
            onComment={() => handleOpenConversation(post)}
            hasFollowingActivity
            isFirstPost={i === 0}
            highlightWord={filterParams.keyword || ''}
            entryPoint={entryPoint}
            symbol={symbol}
            dataCyName={`stream-post-${type}-${post.postid}`}
          />
          {i === 20 && type === 'generic' && (
            <QueryProvider>
              <SuggestedPeopleWidget />
            </QueryProvider>
          )}
        </>
      );
    });
  };

  const activeCommentId: number =
    type === 'generic' ? lastReplyId : selectedPostId;

  const visibilityTopValue: number =
    isResearchStream || isGenericNews ? 0 : 200;

  return (
    <>
      <LineLoader isLoading={isLoading} />
      {!isLoading && newPostCount > 2 && (
        <StreamNewPostBox onClick={handleGetStream}>
          <StreamNewPostText>
            {newPostCount <= 10 ? newPostCount : '10+'} new update(s)
          </StreamNewPostText>
        </StreamNewPostBox>
      )}
      <StreamWidgetBox noBorder={noBorder} newPostCount={newPostCount}>
        {!hideNavigation && (
          <Navigation
            streamType={type}
            hasDefaultTab={hasDefaultTab}
            symbol={symbol}
            isSelfStream={isSelfStream}
          />
        )}
        {filterParams.category === 'news' && sentiment}
        {hasCompanyPinnedPost && (
          <>
            {companyPinnedPost?.data?.research ? (
              <ResearchPost post={companyPinnedPost.data.research} isPinned />
            ) : (
              <Post
                post={companyPinnedPost.data}
                key={companyPinnedPost.data?.postid}
                onComment={() => handleOpenConversation(companyPinnedPost.data)}
                hasFollowingActivity={false}
                isFirstPost
                highlightWord={filterParams.keyword || ''}
                entryPoint={entryPoint}
                symbol={symbol}
                pinned
              />
            )}
          </>
        )}
        {renderContent()}
        <StreamConversation
          visible={openConversation}
          postId={selectedPostId}
          parentId={selectedParentId}
          destroyOnClose
          activeCommentId={activeCommentId}
          onCancel={handleCloseConversation}
          openConversation={() => setOpenConversation(true)}
        />
        {!error && list.length > 0 && !isLastPage && (
          <VisibilitySensor
            onChange={handleLoadMore}
            minTopValue={visibilityTopValue}
          >
            <SkeletonItem />
          </VisibilitySensor>
        )}
      </StreamWidgetBox>
      <BackTop>
        <BackTopWrapper>
          <Image src="/icon/widget-trending/icon-arrow-top.svg" />
        </BackTopWrapper>
      </BackTop>
    </>
  );
}

export default StreamWidget;
